import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  RiLogoutCircleRLine,
  RiChatSmile3Line,
  RiUser2Fill,
  RiMoneyDollarCircleFill,
  RiAddLine,
  RiNotification2Line,
  RiInformationLine,
  RiShoppingBag3Line,
  RiArrowLeftLine,
} from "react-icons/ri";

import { HiPencilAlt } from "react-icons/hi";

import { FaRegAddressCard, FaUsers, FaWhatsapp } from "react-icons/fa";
import { RiKey2Line } from "react-icons/ri";
import { MdDevices, MdPersonAddAlt1, MdPersonalInjury } from "react-icons/md";
import { Bill, useAuth } from "../../hooks/AuthContext";
import { i18n } from "../../localization/i18n";
import { differenceInMinutes, differenceInHours, differenceInDays } from "date-fns";

import {
  ItemText,
  Container,
  BoxNotifications,
  BoxProfile,
  Item,
  Overlay,
  NavigationAction,
  TopBarOptions,
  Separator,
  ItemTextGroup,
  ItemPrimaryText,
  ItemSecondaryText,
  MobileOptions,
  ProfileAvatar,
  CreditsText,
  CreditsWrapper,
  LogoDesktop,
  LogoMobile,
  NotificationsWrapper,
  ButtonIcon,
  NotificationBadge,
  NotificationsDropdownContainer,
  NotificationsHeader,
  NotificationsTitle,
  MarkAllReadButton,
  NotificationsList,
  NotificationItem,
  NotificationIconWrapper,
  NotificationContent,
  NotificationItemTitle,
  NotificationItemBody,
  NotificationTime,
  EmptyNotifications,
  MobileNotificationsHeader,
  MobileHeaderLeft,
  MobileHeaderTitle,
  NotificationActionText
} from "./styles";
import { roles } from "../../utils/constants";
import { useOnboard } from "../../hooks/OnboardContext";
import { PrinterControl } from "../PrinterControl";
import ChangePasswordPopup from "../ChangePasswordPopup";
import global from "../../global";
import { Modal } from "../Modal";
import { PersonModal } from "../PersonModal";
import api from "../../services/api";
import { IPerson } from "../PersonModal/dtos";
import { BsFillCreditCardFill } from "react-icons/bs";
import { usePrinters } from "../../hooks/PrintersContext";

import NavBtn from "../NavBtn";
import Symbol from "../../assets/symbol-purple.svg";
import Logo from "../../assets/logo.svg";

import { toast } from "react-toastify";
import { onMessageListener } from "../../services/firebase";

const notificationStyles: Record<string, {
  icon: React.ReactNode;
  color: string;
  backgroundColor: string;
  route: string;
}> = {
  new_lead_patient: {
    icon: <RiUser2Fill size={20} />,
    color: '#1976d2', // Blue
    backgroundColor: '#e3f2fd',
    route: '/profile/patients',
  },
  order_created: {
    icon: <RiShoppingBag3Line size={20} />,
    color: '#f57c00', // Orange
    backgroundColor: '#fff3e0',
    route: '/orders',
  },
  order_created_urgent: {
    icon: <RiShoppingBag3Line size={20} />,
    color: '#d32f2f', // Red/Urgent
    backgroundColor: '#ffebee',
    route: '/orders',
  },
  order_update: {
    icon: <RiShoppingBag3Line size={20} />,
    color: '#388e3c', // Green
    backgroundColor: '#e8f5e9',
    route: '/orders',
  },
  system_alert: {
    icon: <RiInformationLine size={20} />,
    color: '#d32f2f', // Red
    backgroundColor: '#ffebee',
    route: '/dashboard',
  }
};

const formatCompactTime = (dateInput: string | Date, lang: string): string => {
  const date = new Date(dateInput);
  const now = new Date();
  
  const diffInMinutes = differenceInMinutes(now, date);
  const diffInHours = differenceInHours(now, date);
  const diffInDays = differenceInDays(now, date);

  const isPt = lang.startsWith("pt");
  const isEs = lang.startsWith("es");

  if (diffInMinutes < 1) {
    if (isPt) return "agora";
    if (isEs) return "ahora";
    return "now";
  }

  if (diffInMinutes < 60) {
    return `${diffInMinutes}min`;
  }

  if (diffInHours < 24) {
    return `${diffInHours}h`;
  }

  return `${diffInDays}d`;
};

const getOrderStatusStyle = (status?: string) => {
  switch (status) {
    case "completed":
      return {
        color: "var(--active-green)",
        backgroundColor: "#e8fbf1",
      };
    case "cancelled":
      return {
        color: "var(--error)",
        backgroundColor: "#ffebee",
      };
    case "development":
      return {
        color: "var(--development)",
        backgroundColor: "#e3f2fd",
      };
    case "review":
      return {
        color: "#D67D11",
        backgroundColor: "#fff9e6",
      };
    case "processing":
      return {
        color: "var(--gray)",
        backgroundColor: "#f5f5f7",
      };
    default:
      return null;
  }
};

interface TopbarProps {
  isMenuOpen: boolean;
  toggleMenu: () => void;
  extendedMode: boolean;
  setExtendedMode: (val: boolean) => void;
}

interface INotification {
  id: string;
  type: string;
  title: string;
  body: string;
  metadata: any;
  read_at: string | null;
  created_at: string;
}

const Topbar = ({ isMenuOpen, toggleMenu, extendedMode, setExtendedMode }: TopbarProps) => {
  const { signOut, currency, user, licenseeBills, licenseeFixcoins } =
    useAuth();
  const { licensee, planHasExpired } = useOnboard();
  const { userHasPrinter } = usePrinters()
  const navigate = useNavigate();

  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // isMobile state removed as it is not used in the UI directly, just logic side effects if any. Wait, actually I will just disable the line to avoid big changes in checkDevice logic right now
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMobile, setIsMobile] = useState(false);
  const [isCreatePersonOpen, setisCreatePersonOpen] = useState(false);
  const [person, setPerson] = useState<IPerson>();
  const [licenseeBill, setLicenseeBill] = useState<Bill>();
  const [BuyCreditsOpen, setBuyCreditsOpen] = useState(false);
  
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  const notificationsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleNotificationsMouseEnter = () => {
    if (isMobile) return;
    if (notificationsTimeoutRef.current) {
      clearTimeout(notificationsTimeoutRef.current);
      notificationsTimeoutRef.current = null;
    }
    if (!isNotificationsOpen) {
      setIsNotificationsOpen(true);
      fetchNotifications();
    }
  };

  const handleNotificationsMouseLeave = () => {
    if (isMobile) return;
    notificationsTimeoutRef.current = setTimeout(() => {
      setIsNotificationsOpen(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (notificationsTimeoutRef.current) {
        clearTimeout(notificationsTimeoutRef.current);
      }
    };
  }, []);

  const notification = "0";

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await api.get('/notifications/unread');
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error("Failed to fetch unread notifications count:", error);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!user?.id) return;
    
    const setupMessageListener = async () => {
      try {
        const payload: any = await onMessageListener();
        
        if (payload && payload.notification) {
          // Atualiza a bolinha vermelha e avisa com um Toast
          setUnreadCount((prev) => prev + 1);
          
          toast.info(
            <div>
              <strong>{payload.notification.title}</strong>
              <br/>
              <span style={{ fontSize: '13px' }}>{payload.notification.body}</span>
            </div>, 
            { 
              position: "top-right", 
              autoClose: 5000,
              onClick: () => {
                // Se estiver com o dropdown aberto, recarrega a lista
                if (isNotificationsOpen) {
                  fetchNotifications();
                } else {
                  toggleNotifications(); // Abre a gaveta e ja baixa as notifs
                }
              }
            }
          );

          // Dispara o push nativo do sistema mesmo com o app aberto para persistir na Central do OS
          if (Notification.permission === "granted") {
            try {
              new Notification(payload.notification.title, {
                body: payload.notification.body,
                icon: "/favicon.ico",
              });
            } catch (err) {
              console.warn("Failed to trigger native notification in foreground:", err);
            }
          }

          // Se a lista ja estiver aberta, vamos rebaixar os dados 
          if (isNotificationsOpen) {
             fetchNotifications();
          }
        }
        
        // Fica recursivo esperando a proxima mensagem
        setupMessageListener(); 
      } catch (err) {
        console.log('FCM Listener Error: ', err);
      }
    };

    setupMessageListener();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isNotificationsOpen]);

  async function fetchNotifications() {
    setIsLoadingNotifications(true);
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }

  async function handleNotificationClick(notificationItem: INotification) {
    if (!notificationItem.read_at) {
      try {
        await api.patch(`/notifications/${notificationItem.id}/read`);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        setNotifications((prevList) => 
          prevList.map((item) => 
            item.id === notificationItem.id ? { ...item, read_at: new Date().toISOString() } : item
          )
        );
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    setIsNotificationsOpen(false);

    // If it's a lead patient, open the WhatsApp link in a new tab
    if (notificationItem.type === 'new_lead_patient') {
      const whatsappLink = notificationItem.metadata?.whatsapp_link;
      if (whatsappLink) {
        window.open(whatsappLink, '_blank');
      }
      return;
    }

    // If it's an order notification, go directly to the general orders screen
    if (
      notificationItem.type === 'order_created' || 
      notificationItem.type === 'order_created_urgent' || 
      notificationItem.type === 'order_update'
    ) {
      navigate('/orders');
      return;
    }

    // Resolve style config dynamically for other fallbacks
    const styleConfig = notificationStyles[notificationItem.type] || notificationStyles.system_alert;
    let targetRoute = styleConfig.route;
    
    // Prefix the target route with the role if it's a relative path inside user profile
    if (targetRoute.startsWith('/profile/')) {
      targetRoute = `/${user.role.toLocaleLowerCase()}${targetRoute}`;
    }

    navigate(targetRoute);
  }

  async function handleMarkAllAsRead() {
    try {
      await api.patch('/notifications/read-all');
      setUnreadCount(0);
      setNotifications((prevList) => 
        prevList.map((item) => ({ ...item, read_at: item.read_at || new Date().toISOString() }))
      );
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }

  function toggleNotifications() {
    const willOpen = !isNotificationsOpen;
    setIsNotificationsOpen(willOpen);
    if (willOpen) {
      fetchNotifications();
    }
  }

  const toggleProfile = useCallback(() => {
    setIsProfileOpen(!isProfileOpen);
  }, [isProfileOpen]);

  function togglePopup() {
    if (user.id) {
      setIsPopupOpen(!isPopupOpen);
    }
  }

  const insertUserIntoPerson = useCallback(async (person_id: string, user_id: string) => {
    try {
      const body = {
        person_id: person_id,
        user_id: user_id,
      };

      await api.patch(`/person/update-person`, body);
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    if (!user.person && person) {
      insertUserIntoPerson(person.id, user.id);
    }
  }, [person, user.person, user.id, insertUserIntoPerson]);

  function checkDevice() {
    if (
      window.matchMedia(
        `(min-width:${global.responsive.small.width.value}${global.responsive.small.width.unit})`
      ).matches
    ) {
      setIsMobile(false);
    } else {
      setIsMobile(true);
    }
  }

  useEffect(() => {
    checkDevice();
  }, [toggleProfile]);

  useEffect(() => {
    if (user.role === roles.licensee) {
      setLicenseeBill(
        licenseeBills.find(
          (bill) =>
            bill.referring_period ===
            `${new Date().getMonth()}/${new Date().getFullYear()}`
        )
      );
    }
  }, [user.person, licenseeBills, user.role]);

  return (
    <Container>
      <ChangePasswordPopup
        user_id={user.id}
        setOpen={togglePopup}
        open={isPopupOpen}
      />
      {licensee && isCreatePersonOpen && (
        <Modal open={isCreatePersonOpen}>
          <PersonModal
            licensee={licensee}
            closeModal={() => {
              setisCreatePersonOpen(false);
            }}
            setPerson={setPerson}
            withoutStep
            onCreate={() => {
              window.location.reload();
            }}
          />
        </Modal>
      )}
      <NavigationAction>
        <NavBtn toggleMenu={toggleMenu} isMenuOpen={isMenuOpen} />
        
        <LogoDesktop 
          src={Logo} 
          alt="Fix it" 
          onClick={() => navigate('/')}
        />
        <LogoMobile 
          src={Symbol} 
          alt="Fix it" 
          onClick={() => navigate('/')}
        />
      </NavigationAction>
      <TopBarOptions>
        {user.role === roles.licensee && (
          <CreditsWrapper onClick={() => navigate('/financial/transactions', { state: { openFixcoinModal: true } })}>
            <RiMoneyDollarCircleFill 
               size={26} 
               color="var(--fixit)" 
               style={{ marginLeft: "-1px", zIndex: 2 }} 
            />
            <CreditsText>
              <b>
                {licenseeFixcoins.toLocaleString("pt-BR", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </b>{" "}
              <span>FixCoins</span>
            </CreditsText>
            <RiAddLine size={18} color="var(--fixit)" />
          </CreditsWrapper>
        )}
        {(((user.role === roles.licensee ||
          user.role === roles.employee) && userHasPrinter) ||
          user.role === roles.creator) && <PrinterControl />}

        <NotificationsWrapper
          onMouseEnter={handleNotificationsMouseEnter}
          onMouseLeave={handleNotificationsMouseLeave}
        >
          <ButtonIcon onClick={toggleNotifications}>
            <RiNotification2Line size={22} color="var(--fixit)" />
            {unreadCount > 0 && <NotificationBadge>{unreadCount > 9 ? '9+' : unreadCount}</NotificationBadge>}
          </ButtonIcon>

          {isNotificationsOpen && (
            <NotificationsDropdownContainer>
              <MobileNotificationsHeader>
                <MobileHeaderLeft>
                  <RiArrowLeftLine onClick={toggleNotifications} />
                  <MobileHeaderTitle>{`${i18n.t("topbar.notifications", "Notificações")}`}</MobileHeaderTitle>
                </MobileHeaderLeft>
                {unreadCount > 0 && (
                  <MarkAllReadButton onClick={handleMarkAllAsRead}>
                    {`${i18n.t("topbar.markAllRead", "Marcar todas como lidas")}`}
                  </MarkAllReadButton>
                )}
              </MobileNotificationsHeader>
              <NotificationsHeader>
                <NotificationsTitle>{`${i18n.t("topbar.notifications", "Notificações")}`}</NotificationsTitle>
                {unreadCount > 0 && (
                  <MarkAllReadButton onClick={handleMarkAllAsRead}>
                    {`${i18n.t("topbar.markAllRead", "Marcar todas como lidas")}`}
                  </MarkAllReadButton>
                )}
              </NotificationsHeader>
              <NotificationsList>
                {isLoadingNotifications ? (
                  <EmptyNotifications>{`${i18n.t("topbar.loading", "Carregando...")}`}</EmptyNotifications>
                ) : notifications.length === 0 ? (
                  <EmptyNotifications>{`${i18n.t("topbar.noNotifications", "Nenhuma notificação")}`}</EmptyNotifications>
                ) : (
                  notifications.map((notif) => {
                    const styleConfig = notificationStyles[notif.type] || notificationStyles.system_alert;
                    const statusStyle = getOrderStatusStyle(notif.metadata?.status);
                    const iconColor = statusStyle?.color || styleConfig.color;
                    const iconBg = statusStyle?.backgroundColor || styleConfig.backgroundColor;

                    return (
                      <NotificationItem 
                        key={notif.id} 
                        isRead={!!notif.read_at}
                        onClick={() => handleNotificationClick(notif)}
                      >
                        <NotificationIconWrapper 
                          color={iconColor} 
                          backgroundColor={iconBg}
                        >
                          {styleConfig.icon}
                        </NotificationIconWrapper>
                        <NotificationContent>
                          <NotificationItemTitle isRead={!!notif.read_at}>
                            {notif.title}
                          </NotificationItemTitle>
                          <NotificationItemBody>
                            {notif.body}
                          </NotificationItemBody>
                          {notif.type === 'new_lead_patient' && (
                            <NotificationActionText color="#075e54">
                              <FaWhatsapp /> Iniciar conversa no WhatsApp
                            </NotificationActionText>
                          )}
                          {(notif.type === 'order_created' || 
                            notif.type === 'order_created_urgent' || 
                            notif.type === 'order_update') && (
                            <NotificationActionText color="var(--fixit)">
                              <RiShoppingBag3Line /> Verificar pedido
                            </NotificationActionText>
                          )}
                        </NotificationContent>
                        <NotificationTime>
                          {formatCompactTime(notif.created_at, i18n.language)}
                        </NotificationTime>
                      </NotificationItem>
                    );
                  })
                )}
              </NotificationsList>
            </NotificationsDropdownContainer>
          )}
        </NotificationsWrapper>
        {isNotificationsOpen && <Overlay onClick={toggleNotifications} />}

        {/* <NameLabel>{!!user ? user.name : ""}</NameLabel> */}

        <ProfileAvatar onClick={toggleProfile}>
          {!!user?.person
            ? user.person.name.substring(0, 2).toUpperCase()
            : user.name.substring(0, 2).toUpperCase()}
          {isProfileOpen ? (
            <>
              <BoxProfile>
                {/* <Link style={{ textDecoration: 'none' }} to="/">
                <Item>
                    <ItemText>{i18n.t('topbar.editProfile')}</ItemText>
                  <RiUser3Line size={22} color="var(--fixit)" />
                </Item>s
              </Link> */}
                <Item
                  hasNotification={
                    !licenseeBill && user.role === roles.licensee
                  }
                  onClick={() => {
                    (user.role === roles.licensee ||
                      user.role === roles.fixiter) &&
                      !planHasExpired &&
                      navigate(
                        `/${user.role.toLocaleLowerCase()}/profile/my_profile`
                      );
                  }}
                >
                  <ItemTextGroup>
                    <ItemPrimaryText>
                      {!!user?.person ? user.person.name : user.name}
                    </ItemPrimaryText>
                    <ItemSecondaryText>
                      {licensee ? licensee.name : user.role}
                    </ItemSecondaryText>
                  </ItemTextGroup>
                  <RiUser2Fill size={20} />
                </Item>
                <Separator />
                <MobileOptions>
                  {(user.role === roles.fixiter ||
                    user.role === roles.licensee) &&
                    !planHasExpired && (
                      <>
                        <Item
                          onClick={() => {
                            navigate(
                              `/${user.role.toLocaleLowerCase()}/profile/payments`
                            );
                          }}
                        >
                          <ItemText>{`${i18n.t(
                            "userProfile.paymentInfos"
                          )}`}</ItemText>
                          <BsFillCreditCardFill size={20} />
                        </Item>
                        <Separator />
                        <Item
                          onClick={() => {
                            navigate(
                              `/${user.role.toLocaleLowerCase()}/profile/address`
                            );
                          }}
                        >
                          <ItemText>{`${i18n.t(
                            "userProfile.addressLabel"
                          )}`}</ItemText>
                          <FaRegAddressCard size={20} />
                        </Item>
                        <Separator />
                        <Item
                          onClick={() => {
                            navigate(
                              `/${user.role.toLocaleLowerCase()}/profile/patients`
                            );
                          }}
                        >
                          <ItemText>{`${i18n.t(
                            "userProfile.patientLabel"
                          )}`}</ItemText>
                          <MdPersonalInjury size={20} />
                        </Item>
                        <Separator />
                      </>
                    )}
                  {user.role === roles.licensee && (
                    <>
                      <Item
                        onClick={() => {
                          navigate(
                            `/${user.role.toLocaleLowerCase()}/profile/partners`
                          );
                        }}
                      >
                        <ItemText>{`${i18n.t(
                          "userProfile.partnerLabel"
                        )}`}</ItemText>
                        <FaUsers size={20} />
                      </Item>
                      <Separator />
                      <Item
                        onClick={() => {
                          navigate(
                            `/${user.role.toLocaleLowerCase()}/profile/users`
                          );
                        }}
                      >
                        <ItemText>{`${i18n.t(
                          "userProfile.userLabel"
                        )}`}</ItemText>
                        <MdPersonAddAlt1 size={20} />
                      </Item>
                      <Separator />
                    </>
                  )}
                </MobileOptions>
                {user.role !== roles.licensee &&
                  user.role !== roles.fixiter && (
                    <>
                      <Item onClick={togglePopup}>
                        <ItemText>{`${i18n.t(
                          "topbar.change_password"
                        )}`}</ItemText>
                        <RiKey2Line size={20} />
                      </Item>
                      <Separator />
                    </>
                  )}
                {!user.person && (
                  <Item onClick={() => setisCreatePersonOpen(true)}>
                    <ItemText>{`${i18n.t("topbar.addPerson")}`}</ItemText>
                    <HiPencilAlt size={20} />
                  </Item>
                )}
                {/*
              { user.role === roles.licensee &&
                <>
                <Item
                onClick={() => {
                  history.push({
                    pathname: `/orders/as_provider`
                  });
                } }>
                    <ItemText>{`${i18n.t('topbar.fixpoint')}`}</ItemText>
                    <MdOutlinePersonPinCircle size={20} />
                  </Item>
                <Separator />
                </>
              } */}
                {localStorage.getItem("@FixitApp:Device") &&
                  <Item>
                    <ItemTextGroup>
                      <ItemPrimaryText>
                        Device
                      </ItemPrimaryText>
                      <ItemSecondaryText>
                        {`ID: ${localStorage.getItem("@FixitApp:Device")}`}
                      </ItemSecondaryText>
                    </ItemTextGroup>
                    < MdDevices size={20} />
                  </Item>
                }

                <Separator />

                <Item onClick={signOut}>
                  <ItemText>{`${i18n.t("topbar.signout")}`}</ItemText>
                  <RiLogoutCircleRLine size={20} />
                </Item>
              </BoxProfile>
              <Overlay onClick={toggleProfile} />
            </>
          ) : null}
        </ProfileAvatar>
      </TopBarOptions>
    </Container>
  );
};

export default Topbar;
